/**
 * Network Monitor Service
 *
 * Monitors network connectivity and provides real-time network status.
 * Used by offline manager to determine when to sync queued actions.
 *
 * NetInfo is loaded via require() in try/catch so a missing native module
 * (or failed link) does not crash the app at import time.
 */

import type { NetInfoState } from '@react-native-community/netinfo';
import { useEffect, useState } from 'react';
import { AppState, Platform, type AppStateStatus } from 'react-native';
import { traceModuleEvaluation } from '@/utils/moduleTrace';

traceModuleEvaluation('src/services/networkMonitor');

type NetInfoModule = Pick<typeof import('@react-native-community/netinfo'), 'addEventListener' | 'fetch'>;

function isNetInfoModule(value: unknown): value is NetInfoModule {
  return Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as { addEventListener?: unknown }).addEventListener === 'function' &&
      typeof (value as { fetch?: unknown }).fetch === 'function'
  );
}

function tryLoadNetInfo(): NetInfoModule | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const loaded = require('@react-native-community/netinfo');
    const candidate = loaded?.default ?? loaded;

    if (isNetInfoModule(candidate)) {
      return candidate;
    }

    if (isNetInfoModule(loaded)) {
      return loaded;
    }

    console.warn('[NetworkMonitor] NetInfo module shape is unsupported:', {
      keys: loaded && typeof loaded === 'object' ? Object.keys(loaded) : [],
      hasDefault: Boolean(loaded?.default),
      defaultKeys:
        loaded?.default && typeof loaded.default === 'object'
          ? Object.keys(loaded.default)
          : [],
    });
    return null;
  } catch (e) {
    console.warn('[NetworkMonitor] NetInfo unavailable:', e);
    return null;
  }
}

const netInfo = tryLoadNetInfo();

export type NetworkStatus = {
  isConnected: boolean;
  isInternetReachable: boolean | null;
  type: string;
  details: NetInfoState['details'] | null;
};

type NetworkStatusListener = (status: NetworkStatus) => void;

const offlineFallbackStatus = (): NetworkStatus => ({
  isConnected: false,
  isInternetReachable: false,
  type: 'unknown',
  details: null,
});

/** After sleep/resume, iOS often reports isInternetReachable: false briefly while probing — don't flip UI offline immediately. */
const UNREACHABLE_CONFIRM_MS = 900;
/** After sleep/resume, some devices briefly report disconnected before network stack fully restores. */
const DISCONNECTED_CONFIRM_MS = 900;

/**
 * Right after foreground, NetInfo can keep reporting stale disconnected/unreachable briefly
 * (longer than the confirm timers). One extra deferred fetch reduces false offline UI without
 * waiting on user interaction.
 */
const FOREGROUND_CONFIRM_GRACE_MS = 45_000;
const FOREGROUND_FOLLOWUP_PROBE_MS = 850;

/** When NetInfo would mark us offline, confirm with a minimal HTTP ping (NetInfo lingers stale after resume). */
const HTTP_PROBE_TIMEOUT_MS = 3_500;
/** Used by captive-portal checks; native fetch has no CORS. Intentionally avoids app backend URLs. */
const HTTP_PROBE_URL = 'https://www.google.com/generate_204';

function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isHttpProbeDisabledByTestHarness(): boolean {
  return typeof globalThis !== 'undefined'
    ? (globalThis as { __tripflowNetMonDisableHttpProbe?: boolean }).__tripflowNetMonDisableHttpProbe ===
        true
    : false;
}

async function httpReachabilityProbe(): Promise<boolean> {
  // Web/React Native Web: cross-origin pings are unreliable due to CORS; keep NetInfo-only there.
  if (Platform.OS === 'web') {
    return false;
  }
  if (isHttpProbeDisabledByTestHarness()) {
    return false;
  }
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), HTTP_PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(HTTP_PROBE_URL, {
      method: 'GET',
      cache: 'no-store',
      signal: ctrl.signal,
    });
    clearTimeout(timer);
    if (res.ok) return true;
    // Typical response for generate_204; some stacks map it to ok:false.
    if (res.status === 204 || res.status === 200) return true;
    return false;
  } catch {
    clearTimeout(timer);
    return false;
  }
}

class NetworkMonitor {
  private listeners: Set<NetworkStatusListener> = new Set();
  private currentStatus: NetworkStatus | null = null;
  private unreachableConfirmTimer: ReturnType<typeof setTimeout> | null = null;
  private unreachableProbePending = false;
  private disconnectConfirmTimer: ReturnType<typeof setTimeout> | null = null;
  private disconnectProbePending = false;
  private lastBecameActiveAtMs = 0;
  /** After a successful probe, NetInfo may still briefly report reachable=false; soften offline UI. */
  private lastConfirmedAppLayerReachableAtMs = 0;

  constructor() {
    if (!netInfo) {
      this.currentStatus = offlineFallbackStatus();
      return;
    }

    this.lastBecameActiveAtMs = Date.now();

    void this.refreshStatus().catch((error) => {
      console.warn('[NetworkMonitor] Initial refresh failed:', error);
    });

    try {
      netInfo.addEventListener((state) => {
        this.updateStatus(state);
      });
    } catch (error) {
      console.warn('[NetworkMonitor] Failed to subscribe to NetInfo:', error);
    }

    AppState.addEventListener('change', (next: AppStateStatus) => {
      if (next === 'active') {
        this.lastBecameActiveAtMs = Date.now();
        void this.refreshStatus().catch((error) => {
          console.warn('[NetworkMonitor] Resume refresh failed:', error);
        });
      }
    });
  }

  /** True shortly after foreground — NetInfo readings are unreliable here. */
  private isFreshForeground(): boolean {
    return Date.now() - this.lastBecameActiveAtMs < FOREGROUND_CONFIRM_GRACE_MS;
  }

  /**
   * Get current network status
   */
  async getStatus(): Promise<NetworkStatus> {
    if (!netInfo) {
      return this.currentStatus ?? offlineFallbackStatus();
    }
    // While confirming a flaky unreachable reading, avoid extra fetch() calls that would restart the timer.
    if (this.unreachableProbePending) {
      return this.currentStatus ?? this.provisionalOnlineWhileProbing();
    }
    try {
      const state = await netInfo.fetch();
      return this.updateStatus(state);
    } catch (error) {
      console.warn('[NetworkMonitor] getStatus failed:', error);
      return this.currentStatus ?? offlineFallbackStatus();
    }
  }

  /**
   * Refresh network status
   */
  async refreshStatus(): Promise<NetworkStatus> {
    if (!netInfo) {
      const s = this.currentStatus ?? offlineFallbackStatus();
      this.currentStatus = s;
      return s;
    }
    try {
      const state = await netInfo.fetch();
      return this.updateStatus(state);
    } catch (error) {
      console.warn('[NetworkMonitor] refreshStatus failed:', error);
      const fallback = this.currentStatus ?? offlineFallbackStatus();
      this.currentStatus = fallback;
      return fallback;
    }
  }

  /**
   * Check if device is currently online
   */
  isOnline(): boolean {
    return !this.shouldUseOfflineDataFallback();
  }

  /**
   * Use durable local caches when Firestore may return empty/partial snapshots:
   * no link, or NetInfo explicitly reports no internet (e.g. Wi‑Fi without a route).
   * When `isInternetReachable` is null/unknown, we keep prior behavior (assume reachable).
   *
   * When `currentStatus` is still null (before the first NetInfo fetch/event on cold start),
   * do **not** treat the app as offline — otherwise the UI flashes "offline" for a few seconds.
   */
  shouldUseOfflineDataFallback(): boolean {
    const s = this.currentStatus;
    if (s == null) return false;
    const now = Date.now();
    /** NetInfo reachable flag can lag a successful socket session right after foreground. */
    const trustRecentProbeMs = 8000;
    if (
      s.isConnected &&
      s.isInternetReachable === false &&
      now - this.lastConfirmedAppLayerReachableAtMs < trustRecentProbeMs
    ) {
      return false;
    }
    if (!s.isConnected) return true;
    if (s.isInternetReachable === false) return true;
    return false;
  }

  /**
   * Check if device is currently offline
   */
  isOffline(): boolean {
    return !this.isOnline();
  }

  /**
   * Subscribe to network status changes
   */
  addListener(listener: NetworkStatusListener): () => void {
    this.listeners.add(listener);

    // Immediately call with current status if available
    if (this.currentStatus) {
      listener(this.currentStatus);
    }

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Convert NetInfo state to our NetworkStatus format
   */
  private netInfoStateToStatus(state: NetInfoState): NetworkStatus {
    return {
      isConnected: state.isConnected ?? false,
      isInternetReachable: state.isInternetReachable ?? null,
      type: state.type,
      details: state.details,
    };
  }

  private cancelUnreachableConfirm(): void {
    if (this.unreachableConfirmTimer != null) {
      clearTimeout(this.unreachableConfirmTimer);
      this.unreachableConfirmTimer = null;
    }
  }

  private cancelDisconnectConfirm(): void {
    if (this.disconnectConfirmTimer != null) {
      clearTimeout(this.disconnectConfirmTimer);
      this.disconnectConfirmTimer = null;
    }
  }

  private provisionalOnlineWhileProbing(source?: NetInfoState): NetworkStatus {
    return {
      isConnected: true,
      isInternetReachable: null,
      type: source?.type ?? this.currentStatus?.type ?? 'unknown',
      details: source?.details ?? this.currentStatus?.details ?? null,
    };
  }

  private commitAndNotifyStatus(status: NetworkStatus): NetworkStatus {
    this.cancelUnreachableConfirm();
    this.cancelDisconnectConfirm();
    this.unreachableProbePending = false;
    this.disconnectProbePending = false;
    this.currentStatus = status;

    this.listeners.forEach((listener) => {
      try {
        listener(status);
      } catch (error) {
        console.error('[NetworkMonitor] Error in listener:', error);
      }
    });

    return status;
  }

  private commitAndNotify(state: NetInfoState): NetworkStatus {
    return this.commitAndNotifyStatus(this.netInfoStateToStatus(state));
  }

  private looksOfflineFromFetch(state: NetInfoState): boolean {
    const connected = state.isConnected ?? false;
    if (!connected) return true;
    if (state.isInternetReachable === false) return true;
    return false;
  }

  private resolveOfflineNetInfo(state: NetInfoState): Promise<NetInfoState> {
    return (async (): Promise<NetInfoState> => {
      let fresh = state;

      if (
        this.isFreshForeground() &&
        (fresh.isConnected ?? false) &&
        fresh.isInternetReachable === false
      ) {
        await sleepMs(FOREGROUND_FOLLOWUP_PROBE_MS);
        fresh = await netInfo!.fetch();
      }

      if (this.isFreshForeground() && !(fresh.isConnected ?? false)) {
        await sleepMs(FOREGROUND_FOLLOWUP_PROBE_MS);
        fresh = await netInfo!.fetch();
      }

      if (!this.looksOfflineFromFetch(fresh)) {
        return fresh;
      }

      const ok = await httpReachabilityProbe();
      if (ok) {
        this.lastConfirmedAppLayerReachableAtMs = Date.now();
        return {
          ...fresh,
          isConnected: true,
          isInternetReachable: null,
        } as NetInfoState;
      }
      return fresh;
    })();
  }

  private async finishUnreachableProbe(): Promise<void> {
    if (!netInfo) return;
    try {
      const initial = await netInfo.fetch();
      const resolved = await this.resolveOfflineNetInfo(initial);
      this.commitAndNotify(resolved);
    } catch (e) {
      console.warn('[NetworkMonitor] Unreachable confirm fetch failed:', e);
      this.cancelUnreachableConfirm();
      this.unreachableProbePending = false;
    }
  }

  private async finishDisconnectProbe(): Promise<void> {
    if (!netInfo) return;
    try {
      let fresh = await netInfo.fetch();
      if (this.isFreshForeground() && this.looksOfflineFromFetch(fresh)) {
        await sleepMs(FOREGROUND_FOLLOWUP_PROBE_MS);
        fresh = await netInfo.fetch();
      }
      const resolved =
        !(fresh.isConnected ?? false) || fresh.isInternetReachable === false
          ? await this.resolveOfflineNetInfo(fresh)
          : fresh;
      this.commitAndNotify(resolved);
    } catch (e) {
      console.warn('[NetworkMonitor] Disconnect confirm fetch failed:', e);
      this.cancelDisconnectConfirm();
      this.disconnectProbePending = false;
    }
  }

  /**
   * Update status and notify listeners
   */
  private updateStatus(state: NetInfoState): NetworkStatus {
    const status = this.netInfoStateToStatus(state);

    if (!status.isConnected) {
      this.cancelUnreachableConfirm();
      this.unreachableProbePending = false;
      const wasConnected = this.currentStatus?.isConnected === true;
      if (wasConnected) {
        this.cancelDisconnectConfirm();
        this.disconnectProbePending = true;
        this.disconnectConfirmTimer = setTimeout(() => {
          this.disconnectConfirmTimer = null;
          void this.finishDisconnectProbe();
        }, DISCONNECTED_CONFIRM_MS);
        return this.currentStatus ?? status;
      }
      return this.commitAndNotify(state);
    }

    this.cancelDisconnectConfirm();
    this.disconnectProbePending = false;

    // Transient "no internet" while interface is up (common right after unlock / resume).
    if (status.isInternetReachable === false) {
      this.cancelUnreachableConfirm();
      this.unreachableProbePending = true;
      // If we were previously offline and the interface is now connected, surface a provisional
      // "online" status immediately so the UI does not stay stuck on stale offline state.
      if (!this.currentStatus?.isConnected) {
        this.commitAndNotifyStatus(this.provisionalOnlineWhileProbing(state));
        this.unreachableProbePending = true;
      }
      this.unreachableConfirmTimer = setTimeout(() => {
        this.unreachableConfirmTimer = null;
        void this.finishUnreachableProbe();
      }, UNREACHABLE_CONFIRM_MS);
      return this.currentStatus ?? this.provisionalOnlineWhileProbing(state);
    }

    return this.commitAndNotify(state);
  }
}

export type NetworkMonitorApi = Pick<
  NetworkMonitor,
  'getStatus' | 'refreshStatus' | 'isOnline' | 'isOffline' | 'shouldUseOfflineDataFallback' | 'addListener'
>;

let networkMonitorInstance: NetworkMonitor | null = null;

function getNetworkMonitorInstance(): NetworkMonitor {
  if (!networkMonitorInstance) {
    networkMonitorInstance = new NetworkMonitor();
  }

  return networkMonitorInstance;
}

export const networkMonitor: NetworkMonitorApi = {
  getStatus: () => getNetworkMonitorInstance().getStatus(),
  refreshStatus: () => getNetworkMonitorInstance().refreshStatus(),
  isOnline: () => getNetworkMonitorInstance().isOnline(),
  isOffline: () => getNetworkMonitorInstance().isOffline(),
  shouldUseOfflineDataFallback: () => getNetworkMonitorInstance().shouldUseOfflineDataFallback(),
  addListener: (listener) => getNetworkMonitorInstance().addListener(listener),
};

/**
 * React hook for network status
 */
export function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>({
    isConnected: true,
    isInternetReachable: null,
    type: 'unknown',
    details: null,
  });

  useEffect(() => {
    // Get initial status
    networkMonitor.getStatus().then(setStatus);

    // Subscribe to changes
    const unsubscribe = networkMonitor.addListener(setStatus);

    return unsubscribe;
  }, []);

  return status;
}

/**
 * React hook for online/offline status
 */
export function useIsOnline(): boolean {
  const [online, setOnline] = useState(() => networkMonitor.isOnline());

  useEffect(() => {
    setOnline(networkMonitor.isOnline());
    return networkMonitor.addListener(() => {
      setOnline(networkMonitor.isOnline());
    });
  }, []);

  return online;
}
